'use client';

import { useUser } from '@/hooks/auth/use-user';
import { Avatar, IconButton, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useTranslate } from '@tolgee/react';
import { memo, useCallback, useMemo, useState } from 'react';
import UserMenu from './UserMenu';

// 样式常量和工厂函数
const createButtonStyles = ({
  isMenuOpen = false,
  isTextButton = false,
}: {
  isMenuOpen?: boolean;
  isTextButton?: boolean;
} = {}): SxProps<Theme> => ({
  p: 0,
  ...(isTextButton
    ? {
        height: 40,
        px: 2,
      }
    : {
        width: 40,
        height: 40,
      }),
  borderRadius: '12px',
  backgroundColor: 'rgba(36, 39, 49, 0.85)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(12px)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '12px',
    padding: '1px',
    background: 'linear-gradient(135deg, rgba(250, 148, 28, 0.3), rgba(111, 67, 243, 0.3))',
    mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    maskComposite: 'xor',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  ...(isMenuOpen && {
    backgroundColor: 'rgba(36, 39, 49, 0.95)',
    borderColor: 'rgba(250, 148, 28, 0.6)',
    boxShadow: '0 0 25px rgba(250, 148, 28, 0.3), inset 0 0 15px rgba(250, 148, 28, 0.1)',
    '&::before': {
      opacity: 1,
    },
  }),
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: 'rgba(36, 39, 49, 0.95)',
      borderColor: 'rgba(255, 255, 255, 0.25)',
      transform: 'scale(1.05)',
      boxShadow: '0 0 15px rgba(255, 255, 255, 0.1)',
      '&::before': {
        opacity: 0.7,
      },
    },
  },
  '&:active': {
    transform: 'scale(0.95)',
    boxShadow: '0 0 10px rgba(250, 148, 28, 0.4)',
  },
});

const createTextStyles = ({
  isAvatar = false,
}: {
  isAvatar?: boolean;
} = {}) => ({
  fontSize: '14px',
  ...(isAvatar
    ? {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
      }
    : {}),
});

/**
 * 用户头像按钮组件
 * 使用与LanguageSwitcher相同的设计风格
 * 适配暗夜模式/赛博朋克风格
 */
function UserAvatar() {
  const { user } = useUser();
  const theme = useTheme();
  const { t } = useTranslate();

  // 用户菜单状态管理
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLElement | null>(null);
  const userMenuOpen = Boolean(userMenuAnchorEl);

  // 事件处理器
  const handleUserMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  }, []);

  const handleUserMenuClose = useCallback(() => {
    setUserMenuAnchorEl(null);
  }, []);

  const handleLoginClick = useCallback(() => {
    window.location.href = `${process.env.NEXT_PUBLIC_NEXT_WEB}/login?redirect=${encodeURIComponent(window.location.href)}`;
  }, []);

  // 计算属性
  const userInitial = useMemo(() => user?.userName?.charAt(0)?.toUpperCase() || 'U', [user?.userName]);

  // 未登录：显示登录按钮
  if (!user) {
    return (
      <IconButton
        onClick={handleLoginClick}
        sx={createButtonStyles({
          isMenuOpen: false,
          isTextButton: true,
        })}
        aria-label="Login or Sign up"
      >
        <span className="font-medium text-white whitespace-nowrap" style={createTextStyles({})}>
          {t('signin_signup.name')}
        </span>
      </IconButton>
    );
  }

  // 已登录：显示头像按钮和菜单
  return (
    <>
      <Avatar
        onClick={handleUserMenuOpen}
        alt={t('appbar.user_avatar_alt')}
        src={user?.avatar}
        sx={{
          width: 40,
          height: 40,
          cursor: 'pointer',
          border: (theme) => `2px solid ${theme.palette.background.paper}`,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          transition: theme.transitions.create(['box-shadow'], {
            duration: theme.transitions.duration.shorter,
          }),
          '&:hover': {
            boxShadow: (theme) =>
              `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.primary.main}`,
          },
          position: 'relative',
          zIndex: 1,
        }}
      >
        {userInitial}
      </Avatar>
      <UserMenu open={userMenuOpen} anchorEl={userMenuAnchorEl} onClose={handleUserMenuClose} />
    </>
  );
}

// 使用 memo 优化性能
export default memo(UserAvatar);
